"use client";

import { useEffect, useRef, useState } from "react";
import { ImagePlus, Upload } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { apiBaseURL } from "@/lib/api-config";
import { useUploadProductImage } from "@/hooks/api/useProductMutations";
import type { ProductRow } from "@/lib/types/product";

type Props = {
  product: ProductRow | null;
  onOpenChange: (open: boolean) => void;
};

function imgSrc(url: string | null | undefined): string {
  if (!url) {
    return "";
  }
  if (url.startsWith("http")) {
    return url;
  }

  return `${apiBaseURL}${url}`;
}

export function ProductImageDialog({ product, onOpenChange }: Props) {
  const upload = useUploadProductImage();
  const fileRef = useRef<HTMLInputElement>(null);
  const [err, setErr] = useState<string | null>(null);
  const [localUrl, setLocalUrl] = useState<string | null>(null);

  useEffect(() => {
    setErr(null);
    setLocalUrl(null);
  }, [product?.id]);

  const open = Boolean(product);
  const src = localUrl ?? imgSrc(product?.image_url);
  const hasImage = Boolean(src);

  async function handlePick(file: File) {
    if (!product) {
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setErr("Image too large (max 2 MB).");

      return;
    }
    setErr(null);
    try {
      const url = await upload.mutateAsync({ id: product.id, file });
      setLocalUrl(url.startsWith("http") ? url : `${apiBaseURL}${url}`);
    } catch (e) {
      const msg =
        e && typeof e === "object" && "response" in e
          ? String((e as { response?: { data?: { error?: string } } }).response?.data?.error ?? "")
          : e instanceof Error
            ? e.message
            : "Upload failed";
      setErr(msg || "Upload failed");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="line-clamp-1">
            {product?.name ?? "Product image"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex min-h-[280px] items-center justify-center rounded-lg border bg-muted/40 p-3">
          {hasImage ? (
            <img
              src={src}
              alt={product?.name ?? ""}
              className="max-h-[60vh] w-auto rounded-md object-contain"
            />
          ) : (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <ImagePlus className="h-12 w-12 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                No image yet. Add one to make it visible in POS, grid, and bills.
              </p>
              <Button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={upload.isPending}
              >
                <Upload className="mr-2 h-4 w-4" />
                {upload.isPending ? "Uploading…" : "Add image"}
              </Button>
            </div>
          )}
        </div>

        {err ? <p className="text-sm text-destructive">{err}</p> : null}

        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) {
              void handlePick(f);
            }
          }}
        />

        <DialogFooter className="gap-2 sm:gap-2">
          {hasImage ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => fileRef.current?.click()}
              disabled={upload.isPending}
            >
              <Upload className="mr-2 h-4 w-4" />
              {upload.isPending ? "Uploading…" : "Replace image"}
            </Button>
          ) : null}
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
